import { NavLink } from '../../i18n/Link';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoryblokProxy } from '../../hooks/useStoryblokProxy';
import * as icons from '../icons/Icons';
import { MoreIcon } from '../icons/Icons';
import { SubNavigation } from './SubNavigation';
import { STORY_TYPE } from '../../constants/storyblok';
import { createNavLink } from '../../utils/link-utils';

export const Navigation = () => {
  const [openNav, setOpenNav] = useState(false);

  const { t, i18n } = useTranslation();
  const { stories, isLoading, error } = useStoryblokProxy({
    starts_with: STORY_TYPE.settings,
    per_page: 1,
    language: i18n.language,
  });

  const { pathname } = useLocation();
  useEffect(() => {
    setOpenNav(false); // Close the navigation panel
  }, [pathname]);

  const handleResize = () => {
    if (window.innerWidth >= 960) {
      setOpenNav(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', openNav);
  }, [openNav]);

  const logo = (
    <svg
      className="hidden lg:flex px-4 py-2 w-28 fill-brand-secondary"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 153 89"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M81.5283 56.8996H87.6483V88.325H81.5283V56.8996ZM146.271 69.0683C144.858 64.8581 141.136 62.0691 136.877 62.0691C132.618 62.0691 128.896 64.8759 127.482 69.1038L146.271 69.0683ZM136.895 56.2779C145.735 56.2779 152.91 63.6146 152.91 72.6212C152.91 73.1008 152.893 73.5805 152.857 74.0779L152.785 74.8595H127.16C128.144 79.6914 132.188 83.1733 136.895 83.1733C142.639 83.1733 145.717 79.1585 146.522 77.915L146.987 77.2044L151.926 80.3665L151.497 81.0771C148.365 86.0689 142.818 89.0711 136.895 88.9645C128.073 88.9645 120.879 81.6278 120.879 72.6212C120.879 63.6146 128.055 56.2779 136.895 56.2779ZM63.2041 35.227C68.6799 35.227 73.1357 30.4661 73.1357 24.6216C73.1357 18.7771 68.6799 14.0162 63.2041 14.0162C57.7283 14.0162 53.2725 18.7771 53.2725 24.6216C53.2725 30.4661 57.7283 35.227 63.2041 35.227ZM73.1178 8.88227H79.2199V40.3076H73.0999L73.1178 36.3284C70.2546 39.2595 66.4072 40.9294 62.4346 40.9294C53.9704 40.9294 47.0809 33.5926 47.0809 24.5861C47.0809 15.5795 53.9704 8.26051 62.4346 8.26051C66.4072 8.26051 70.2546 9.93037 73.1178 12.8615V8.88227ZM27.4325 14.034C23.1736 14.034 19.4336 16.8407 18.0378 21.0865L36.8094 21.0509C35.3957 16.8407 31.6736 14.034 27.4325 14.034ZM42.0167 33.0419C38.8851 38.0515 33.3378 41.036 27.4325 40.9294C18.5925 40.9294 11.4167 33.5926 11.4167 24.5861C11.4167 15.5795 18.5925 8.26051 27.4325 8.26051C36.2546 8.26051 43.4483 15.5972 43.4483 24.5861C43.4483 25.3144 43.3946 26.0783 43.2872 26.8244H17.6978C18.6999 31.6563 22.7262 35.1382 27.4325 35.1382C33.1588 35.1382 36.2546 31.1056 37.0599 29.8799L37.5251 29.1515L42.4641 32.3136L42.0167 33.0419ZM0.733566 0.159912H6.85357V40.3076H0.733566V0.159912ZM129.325 14.034C125.066 14.034 121.344 16.8407 119.93 21.0865L138.702 21.0509C137.306 16.8407 133.584 14.034 129.325 14.034ZM129.325 40.9294C120.503 40.9294 113.309 33.5926 113.309 24.5861C113.309 15.5795 120.503 8.26051 129.325 8.26051C138.165 8.26051 145.341 15.5972 145.341 24.5861C145.341 25.0657 145.323 25.5453 145.269 26.0427L145.198 26.8244H119.608C120.61 31.6563 124.637 35.1382 129.343 35.1382C135.087 35.1382 138.165 31.1056 138.97 29.8799L139.436 29.1515L144.375 32.3136L143.945 33.0242C140.796 38.0515 135.266 41.036 129.325 40.9294ZM84.5883 46.0633C86.6999 46.0633 88.4178 47.7687 88.4178 49.8649C88.4178 51.9611 86.6999 53.6665 84.5883 53.6665C82.4767 53.6665 80.7588 51.9611 80.7588 49.8649C80.7588 47.7509 82.4946 46.0633 84.5883 46.0633ZM96.9715 35.1204C100.64 35.1204 104.111 34.1611 104.111 31.6386C104.111 29.0272 98.7788 27.9791 96.0767 27.2863C90.8336 25.9539 84.3199 24.2841 84.3199 17.5691C84.3199 12.3641 89.9746 8.27827 97.1146 8.27827C104.237 8.27827 109.086 11.8667 109.73 12.4352L105.811 16.9296C105.525 16.6986 101.409 14.0873 96.9715 14.0873C93.9651 14.0873 90.6009 15.0821 90.6009 17.7112C90.6009 20.4292 96.0588 21.2641 98.6536 21.9392C103.897 23.2715 110.428 24.9413 110.428 31.6563C110.428 36.8613 105.722 40.9471 97.2757 40.9471C88.6862 40.9471 84.3199 36.2218 84.3199 36.2218L88.2746 31.7629C88.2746 31.7451 91.8536 35.1204 96.9715 35.1204ZM60.9673 56.2956C69.8073 56.2956 76.983 63.5968 76.983 72.6212C76.983 81.6278 69.8073 88.9467 60.9673 88.9467C52.1273 88.9467 44.9515 81.6455 44.9515 72.6212H51.0715C51.0715 78.4479 55.5094 83.1733 60.9673 83.1733C66.443 83.1733 70.863 78.4479 70.863 72.6212C70.863 66.7944 66.4251 62.0691 60.9673 62.0691C60.1262 62.0691 59.303 62.1934 58.5157 62.3889L63.5799 71.1112L58.283 74.1489L46.3651 53.6487H40.8357V47.5733H51.6262V50.54L55.5272 57.2727C57.2272 56.6509 59.0525 56.2956 60.9673 56.2956ZM108.567 71.733L121.63 88.325H113.864L103.27 74.8595H100.031V88.325H93.9115V48.1773H100.031V69.1038H102.769L113.596 56.8996H121.756L108.567 71.733ZM16.1946 83.2799C21.6704 83.2799 26.1262 78.519 26.1262 72.6745C26.1262 66.83 21.6704 62.0691 16.1946 62.0691C10.7188 62.0691 6.26304 66.83 6.26304 72.6745C6.26304 78.519 10.7188 83.2799 16.1946 83.2799ZM26.1262 56.9352H32.2283V88.3605H26.1083L26.1262 84.3635C23.263 87.3124 19.4157 88.9823 15.443 88.9823C6.97883 88.9823 0.0893555 81.6455 0.0893555 72.6389C0.0893555 63.6324 6.97883 56.3134 15.443 56.3134C19.4157 56.3134 23.263 57.9833 26.1262 60.9144V56.9352Z"
      />
    </svg>
  );

  const moreButton = (
    <button
      data-testid="open-subnav-button"
      className={`inline-flex flex-col items-center justify-center group lg:items-start lg:px-6 ${
        openNav ? 'active' : ''
      }`}
      title={t('navigation.toggle')}
      onClick={() => setOpenNav(!openNav)}
    >
      <MoreIcon className="mb-1 text-darkGray group-hover:text-slate-950 lg:hidden" />
      <span className="text-xs flex items-center text-darkGray group-hover:text-slate-950 font-bold lg:text-base">
        {t('navigation.more')}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`hidden lg:flex feather feather-chevron-up w-4 h-4 ml-2 ${
            openNav ? 'rotate-180' : 'rotate-0'
          }`}
        >
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      </span>
    </button>
  );

  return (
    <>
      {!isLoading && !error && (
        <>
          <SubNavigation
            openNav={openNav}
            stories={stories[0].content.subnavigation}
            isLoading={isLoading}
            error={error}
          />
          <nav
            className="fixed lg:relative bottom-0 lg:bottom-[unset] lg:order-1 left-0 z-50 w-full pt-3 bg-lightGray rounded-t-[10px] lg:rounded-none pb-safe"
            data-testid="navigation"
          >
            <div className="grid h-full grid-flow-col justify-stretch mx-auto lg:max-w-screen-xl grid-cols-5 lg:flex">
              {logo}
              {stories[0]?.content?.navigation?.map((item) => {
                const Icon = icons[item.icon];
                return (
                  <NavLink
                    exact
                    key={item._uid}
                    to={createNavLink(item.link.cached_url)}
                    className="inline-flex flex-col items-center justify-center group lg:py-2 lg:items-start lg:px-6"
                  >
                    <span className="mb-1 text-darkGray group-hover:text-slate-950 lg:hidden">
                      <Icon />
                    </span>
                    <span className="text-xs text-darkGray group-hover:text-slate-950 font-bold lg:text-base">
                      {item.title}
                    </span>
                  </NavLink>
                );
              })}
              {moreButton}
            </div>
          </nav>
        </>
      )}
    </>
  );
};
